import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, X, Wrench, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatRupeeOrDash as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Electrical & Lighting',
  'Plumbing & Sanitary',
  'Glass & Fabrication',
  'Carpentry & Civil Labor',
  'Transport & Logistics',
  'Site Supervision & Management',
  'Ad-hoc Material',
  'One-Time Charges / Misc',
];

const EMPTY_FORM = {
  description: '',
  category: 'Carpentry & Civil Labor',
  vendor_id: '',
  unit: 'day',
  quantity: 1,
  input_cost: 0,
  selling_price: 0,
  gst_percent: 0,
  notes: '',
};

/**
 * Ad-hoc service line items on a customer order (project).
 * These do NOT touch inventory or PO flow — they're pure cost + revenue lines.
 *
 * Props:
 *   orderId  — order to which services belong
 *   vendors  — list of vendors for the vendor dropdown
 *   onChange — called after any mutation so parent can refresh totals
 */
export default function OrderServicesSection({ orderId, vendors = [], onChange }) {
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [svcRes, sumRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/orders/${orderId}/services`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/orders/${orderId}/summary`, { withCredentials: true }),
      ]);
      setServices(svcRes.data || []);
      setSummary(sumRes.data);
    } catch (e) {
      // silent — services section is auxiliary
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const linePreview = () => {
    const q = Number(form.quantity) || 0;
    const gst = Number(form.gst_percent) || 0;
    const tCost = q * (Number(form.input_cost) || 0) * (1 + gst / 100);
    const tSell = q * (Number(form.selling_price) || 0) * (1 + gst / 100);
    return { total_cost: tCost, total_selling: tSell, margin: tSell - tCost };
  };

  const handleAdd = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (form.quantity <= 0) { toast.error('Quantity must be > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        order_id: orderId,
        description: form.description.trim(),
        category: form.category,
        vendor_id: form.vendor_id || null,
        unit: form.unit || 'unit',
        quantity: Number(form.quantity),
        input_cost: Number(form.input_cost) || 0,
        selling_price: Number(form.selling_price) || 0,
        gst_percent: Number(form.gst_percent) || 0,
        notes: form.notes || '',
      };
      await axios.post(`${BACKEND_URL}/api/order-services`, payload, { withCredentials: true });
      toast.success('Service added');
      setForm(EMPTY_FORM); setShowAdd(false);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save service');
    } finally { setSaving(false); }
  };

  const handleRemove = async (osid) => {
    if (!window.confirm('Remove this service line? Any associated vendor payable will be removed too.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/order-services/${osid}`, { withCredentials: true });
      toast.success('Service removed');
      await load();
      onChange?.();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const previewTotals = linePreview();

  return (
    <div className="mt-4 pt-4 border-t border-stone-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide flex items-center gap-1.5">
          <Wrench className="w-3.5 h-3.5" /> Services & Misc costs
        </h4>
        {!showAdd && (
          <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid={`add-service-${orderId}`} className="h-7 text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add Service
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-stone-400 italic py-2"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading…</p>
      ) : services.length === 0 && !showAdd ? (
        <p className="text-xs text-stone-400 italic py-2">No services yet. Track transport, labor, or misc project costs here.</p>
      ) : (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="text-left p-1.5 font-semibold text-stone-600">Description</th>
              <th className="text-left p-1.5 font-semibold text-stone-600 w-24">Category</th>
              <th className="text-left p-1.5 font-semibold text-stone-600 w-32">Vendor</th>
              <th className="text-right p-1.5 font-semibold text-stone-600 w-16">Qty</th>
              <th className="text-right p-1.5 font-semibold text-stone-600 w-20">Cost/u</th>
              <th className="text-right p-1.5 font-semibold text-stone-600 w-20">Sell/u</th>
              <th className="text-right p-1.5 font-semibold text-stone-600 w-14">GST</th>
              <th className="text-right p-1.5 font-semibold text-stone-600 w-24">Line total</th>
              <th className="text-right p-1.5 font-semibold text-lime-700 w-20">Margin</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {services.map(s => {
              const vendor = vendors.find(v => v.vendor_id === s.vendor_id);
              return (
                <tr key={s.order_service_id} data-testid={`service-row-${s.order_service_id}`} className="border-b border-stone-100 hover:bg-white/50">
                  <td className="p-1.5">
                    <div className="font-medium text-stone-900">{s.description}</div>
                    {s.notes && <div className="text-[10px] text-stone-500">{s.notes}</div>}
                  </td>
                  <td className="p-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] bg-stone-100 text-stone-700">{s.category}</span></td>
                  <td className="p-1.5 text-stone-700 truncate">{vendor?.vendor_name || <span className="text-stone-400">—</span>}</td>
                  <td className="p-1.5 text-right tabular-nums">{s.quantity} <span className="text-[10px] text-stone-400">{s.unit}</span></td>
                  <td className="p-1.5 text-right tabular-nums">{fmt(s.input_cost)}</td>
                  <td className="p-1.5 text-right tabular-nums">{fmt(s.selling_price)}</td>
                  <td className="p-1.5 text-right tabular-nums text-stone-500">{s.gst_percent}%</td>
                  <td className="p-1.5 text-right tabular-nums font-medium">{fmt(s.total_selling)}</td>
                  <td className={`p-1.5 text-right tabular-nums font-semibold ${s.margin >= 0 ? 'text-lime-700' : 'text-red-600'}`}>{fmt(s.margin)}</td>
                  <td className="p-1.5 text-center">
                    <button onClick={() => handleRemove(s.order_service_id)} data-testid={`remove-service-${s.order_service_id}`} className="text-stone-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Inline add form */}
      {showAdd && (
        <div className="mt-3 border border-amber-200 bg-amber-50/40 rounded-md p-3 space-y-2" data-testid={`service-add-form-${orderId}`}>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <div className="sm:col-span-5">
              <Input data-testid="service-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (e.g. Electrician for wiring)" className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-3">
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="service-category" className="h-8 text-xs border-stone-200"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-4">
              <Select value={form.vendor_id || '__none__'} onValueChange={(v) => setForm(f => ({ ...f, vendor_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger data-testid="service-vendor" className="h-8 text-xs border-stone-200"><SelectValue placeholder="Vendor (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No vendor</SelectItem>
                  {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-end">
            <div className="sm:col-span-2">
              <label className="text-[10px] text-stone-500">Unit</label>
              <Input data-testid="service-unit" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="day" className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-stone-500">Qty</label>
              <Input data-testid="service-quantity" type="number" step="0.01" min="0" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-stone-500">Cost/unit</label>
              <Input data-testid="service-input-cost" type="number" step="0.01" min="0" value={form.input_cost} onChange={e => setForm(f => ({ ...f, input_cost: e.target.value }))} className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-stone-500">Sell/unit</label>
              <Input data-testid="service-selling-price" type="number" step="0.01" min="0" value={form.selling_price} onChange={e => setForm(f => ({ ...f, selling_price: e.target.value }))} className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-stone-500">GST %</label>
              <Input data-testid="service-gst" type="number" step="0.01" min="0" max="100" value={form.gst_percent} onChange={e => setForm(f => ({ ...f, gst_percent: e.target.value }))} className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-2 text-[10px] text-stone-600">
              <div>Cost: <strong>{fmt(previewTotals.total_cost)}</strong></div>
              <div>Sell: <strong>{fmt(previewTotals.total_selling)}</strong></div>
              <div className={previewTotals.margin >= 0 ? 'text-lime-700' : 'text-red-600'}>Margin: <strong>{fmt(previewTotals.margin)}</strong></div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <div className="sm:col-span-9">
              <Input data-testid="service-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" className="border-stone-200 h-8 text-xs" />
            </div>
            <div className="sm:col-span-3 flex gap-1 justify-end">
              <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAdd(false); setForm(EMPTY_FORM); }} data-testid="service-cancel" className="h-8 text-xs"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
              <Button type="button" size="sm" onClick={handleAdd} disabled={saving} data-testid="service-save" className="h-8 text-xs bg-amber-700 hover:bg-amber-800 text-white">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Project P&L summary */}
      {summary && (services.length > 0 || summary.product_revenue > 0) && (
        <div className="mt-3 border-t border-stone-200 pt-3">
          <table className="w-full text-[11px]" data-testid={`project-pnl-${orderId}`}>
            <tbody>
              <tr className="text-stone-500">
                <td className="p-1">Products</td>
                <td className="p-1 text-right">Revenue {fmt(summary.product_revenue)}</td>
                <td className="p-1 text-right">Cost {fmt(summary.product_cost)}</td>
                <td className={`p-1 text-right ${summary.product_margin >= 0 ? 'text-lime-700' : 'text-red-600'}`}>Margin {fmt(summary.product_margin)}</td>
              </tr>
              <tr className="text-stone-500">
                <td className="p-1">Services</td>
                <td className="p-1 text-right">Revenue {fmt(summary.service_revenue)}</td>
                <td className="p-1 text-right">Cost {fmt(summary.service_cost)}</td>
                <td className={`p-1 text-right ${summary.service_margin >= 0 ? 'text-lime-700' : 'text-red-600'}`}>Margin {fmt(summary.service_margin)}</td>
              </tr>
              <tr className="border-t border-stone-300 font-semibold text-stone-900">
                <td className="p-1">Project total</td>
                <td className="p-1 text-right">{fmt(summary.total_revenue)}</td>
                <td className="p-1 text-right">{fmt(summary.total_cost)}</td>
                <td className={`p-1 text-right ${summary.total_margin >= 0 ? 'text-lime-700' : 'text-red-600'}`}>{fmt(summary.total_margin)} <span className="text-[9px] font-normal text-stone-500">({summary.margin_pct}%)</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
